/**
 * useClientOfferReviewUnits
 *
 * Maps snapshot + enriched units onto ClientOfferReviewUnit[], builds the
 * shared period axis for the given campaign window + granularity and
 * re-buckets each unit's real availability onto it by date-range overlap —
 * see build-review-periods.ts's `buildReviewPeriodAxis` (also shared with
 * useClientOfferSelections). Extracted from client-offer-review-shell.tsx to
 * keep that file within the lint line-count budget.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AvailabilityPeriodCell,
  ClientOfferViewUnit,
  OfferSnapshotUnit,
  PublicOfferViewUnit,
} from '@ooh/contracts';
import type { PeriodGranularity } from '@ooh/contracts/period-granularity';
import { mapClientViewUnitToReviewUnit } from '../lib/map-client-view-to-review-unit';
import { buildReviewPeriodAxis, getDefaultPeriodIdsFromHalves } from '../lib/build-review-periods';
import type { ClientOfferReviewUnit } from '../../client-offer-review';

/**
 * Overlays halves/canAccept/locked onto each already-mapped unit — extracted
 * so useClientOfferReviewUnits's useMemo body stays within the lint
 * line-count budget. `canAccept` is derived from the SAME axis-based
 * `defaultPeriodIdsByUnitId` (getDefaultPeriodIdsFromHalves against the
 * CURRENT granularity's halves) the accept/period-select default-period
 * logic uses — so the per-unit "Akceptuj" button is only enabled when the
 * currently-displayed axis actually has a selectable default for that unit.
 *
 * CRITICAL bug fix: this used to be raw-cell-based ("any FREE cell exists"),
 * which could disagree with the axis actually rendered — a display
 * granularity coarser than a unit's own server-resolved cell granularity can
 * re-bucket every FREE cell into a non-FREE axis period
 * (buildUnitHalves's worst-status rule), so the raw-cell gate reported the
 * unit acceptable while the axis had zero selectable periods for it. The
 * client could click "Akceptuj" and the accepted decision would silently
 * carry zero periods, dropped from `unitDecisions` by
 * `buildUnitDecisionsPayload`.
 */
function combineReviewUnits(
  mapped: ClientOfferReviewUnit[],
  halvesById: Map<string, ClientOfferReviewUnit['halves']>,
  defaultPeriodIdsByUnitId: Map<string, string[]>,
  lockedRowIds: Set<string>,
): ClientOfferReviewUnit[] {
  return mapped.map((u) => ({
    ...u,
    halves: halvesById.get(u.id) ?? [],
    canAccept: (defaultPeriodIdsByUnitId.get(u.id) ?? []).length > 0,
    locked: lockedRowIds.has(u.id),
  }));
}

export function useClientOfferReviewUnits(
  snapshotUnits: OfferSnapshotUnit[],
  enrichedUnitsById: Map<number, ClientOfferViewUnit | PublicOfferViewUnit> | undefined,
  showSupplierIdentity: boolean,
  // Row ids (snapshot unit id, matching ClientOfferReviewUnit.id) that are locked —
  // carried forward from a prior version, binding (offer-decision-carry-forward). Defaults
  // to empty so every existing caller that predates this capability is unaffected.
  lockedRowIds: Set<string> = new Set(),
  granularity: PeriodGranularity = 'HALF_MONTH',
  campaignFrom: Date | null = null,
  campaignTo: Date | null = null,
  // True only for the internal read-only version-preview screen — see
  // mapClientViewUnitToReviewUnit's useFrozenDimensions doc comment.
  useFrozenDimensions = false,
) {
  const { i18n } = useTranslation('offers');
  return useMemo(() => {
    const mapped = snapshotUnits.map((u) =>
      mapClientViewUnitToReviewUnit(
        u,
        enrichedUnitsById?.get(u.id) ?? null,
        showSupplierIdentity,
        useFrozenDimensions,
      ),
    );
    const unitAvailabilityById = new Map<string, AvailabilityPeriodCell[]>(
      snapshotUnits.map((u) => [String(u.id), enrichedUnitsById?.get(u.id)?.availability ?? []]),
    );
    const { periods, halvesByUnitId: halvesById } = buildReviewPeriodAxis(
      unitAvailabilityById,
      granularity,
      campaignFrom,
      campaignTo,
      i18n.language,
    );
    const defaultPeriodIdsByUnitId = new Map(
      [...unitAvailabilityById.keys()].map((id) => [
        id,
        getDefaultPeriodIdsFromHalves(periods, halvesById.get(id) ?? []),
      ]),
    );
    const units = combineReviewUnits(mapped, halvesById, defaultPeriodIdsByUnitId, lockedRowIds);
    return { units, periods, defaultPeriodIdsByUnitId };
  }, [
    snapshotUnits,
    enrichedUnitsById,
    showSupplierIdentity,
    lockedRowIds,
    granularity,
    campaignFrom,
    campaignTo,
    useFrozenDimensions,
    i18n.language,
  ]);
}
